import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SEO } from "@/components/SEO";
import {
  Users, Phone, Briefcase, Tag, ThumbsUp, ThumbsDown,
  Buildings, CalendarCheck, MagnifyingGlass, Funnel, DownloadSimple
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

export default function AdminVisitorsPage() {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chapterFilter, setChapterFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const params = chapterFilter !== "all" ? `?chapter_id=${chapterFilter}` : "";
      const [visRes, chRes] = await Promise.all([
        axios.get(`${API}/admin/visitors${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/chapters`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setData(visRes.data);
      setChapters(chRes.data);
    } catch (err) {
      console.error("Failed to fetch visitors:", err);
    } finally {
      setLoading(false);
    }
  }, [token, chapterFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  usePolling(fetchData, 15000);

  const totalVisitors = data.reduce((acc, ch) => acc + ch.visitors.length, 0);
  const totalInterested = data.reduce((acc, ch) => acc + ch.interested_count, 0);

  return (
    <>
      <SEO title="Visitors" description="Track and manage meeting visitors across all BCH chapters." path="/admin/visitors" noIndex />
      <div className="space-y-6 animate-fadeInUp">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl text-primary flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-md">
                <Users className="w-5 h-5 text-white" weight="fill" />
              </div>
              Meeting Visitors
            </h1>
            <p className="text-secondary text-sm mt-1">All visitors across chapters</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => window.open(`${API}/admin/export/visitors`, "_blank")} className="hidden sm:flex items-center gap-2 text-bch-gold text-sm font-medium border border-bch-gold/30 rounded-xl px-4 py-2 hover:bg-bch-gold/5" data-testid="export-visitors-btn">
              <DownloadSimple className="w-4 h-4" weight="bold" /> Export CSV
            </button>
            <div className="bg-violet-50 stat-card-violet px-4 py-2 rounded-xl border border-[var(--border-color)]">
              <span className="text-muted text-xs">Total Visitors</span>
              <p className="text-primary font-bold text-lg">{totalVisitors}</p>
            </div>
            <div className="bg-emerald-50 stat-card-emerald px-4 py-2 rounded-xl border border-[var(--border-color)]">
              <span className="text-muted text-xs">Interested</span>
              <p className="text-emerald-600 font-bold text-lg">{totalInterested}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => window.open(`${API}/admin/export/visitors`, "_blank")} className="sm:hidden flex items-center gap-2 text-bch-gold text-sm font-medium border border-bch-gold/30 rounded-xl px-4 py-2 hover:bg-bch-gold/5">
            <DownloadSimple className="w-4 h-4" weight="bold" /> Export CSV
          </button>
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" weight="bold" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search visitors by name, business..."
              className="pl-10 bg-surface border-[var(--border-color)] text-primary"
              data-testid="visitor-search"
            />
          </div>
          <Select value={chapterFilter} onValueChange={setChapterFilter}>
            <SelectTrigger className="w-52 bg-surface border-[var(--border-color)] text-primary" data-testid="visitor-chapter-filter">
              <Buildings className="w-4 h-4 mr-2 text-muted" />
              <SelectValue placeholder="All Chapters" />
            </SelectTrigger>
            <SelectContent className="bg-card border-[var(--border-color)]">
              <SelectItem value="all" className="text-primary">All Chapters</SelectItem>
              {chapters.map((ch) => (
                <SelectItem key={ch.id} value={ch.id} className="text-primary">{ch.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Chapter-wise visitor cards */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-20 bg-card border border-[var(--border-color)] rounded-2xl">
            <Users className="w-16 h-16 text-muted/20 mx-auto mb-4" weight="duotone" />
            <p className="text-secondary">No visitors recorded yet</p>
          </div>
        ) : (
          data.map((chapter) => {
            const filtered = chapter.visitors.filter(v =>
              !search || v.name.toLowerCase().includes(search.toLowerCase()) ||
              v.business_name.toLowerCase().includes(search.toLowerCase())
            );
            if (filtered.length === 0 && search) return null;

            return (
              <div key={chapter.chapter_name} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid={`chapter-visitors-${chapter.chapter_id}`}>
                {/* Chapter Header */}
                <div className="p-5 border-b border-[var(--border-color)] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
                      <Buildings className="w-4 h-4 text-white" weight="fill" />
                    </div>
                    <div>
                      <h2 className="text-primary font-semibold">{chapter.chapter_name}</h2>
                      <p className="text-muted text-xs">{chapter.visitors.length} visitors &middot; {chapter.interested_count} interested</p>
                    </div>
                  </div>
                </div>

                {/* Visitor Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-surface/50">
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">#</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Visitor</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Contact</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Business</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Category</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Meeting</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider p-4 pr-2">Invited By</th>
                        <th className="text-center text-muted text-xs font-semibold uppercase tracking-wider p-4">Interest</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-color)]">
                      {filtered.map((v, i) => (
                        <tr key={v.id} className="hover:bg-surface/30 transition-colors">
                          <td className="p-4 pr-2 text-muted text-xs font-mono">{i + 1}</td>
                          <td className="p-4 pr-2">
                            <p className="text-primary text-sm font-medium">{v.name}</p>
                            <p className="text-muted text-xs">Added by {v.added_by_name}</p>
                          </td>
                          <td className="p-4 pr-2">
                            <span className="text-secondary text-sm flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5 text-muted" /> {v.contact}
                            </span>
                          </td>
                          <td className="p-4 pr-2">
                            <span className="text-secondary text-sm">{v.business_name}</span>
                          </td>
                          <td className="p-4 pr-2">
                            <span className="text-muted text-xs bg-surface px-2 py-1 rounded-lg">{v.category || "—"}</span>
                          </td>
                          <td className="p-4 pr-2">
                            <span className="text-muted text-xs">{v.meeting_title}</span>
                            <p className="text-muted text-[10px]">{v.meeting_date}</p>
                          </td>
                          <td className="p-4 pr-2">
                            {v.invited_by_name ? (
                              <span className="text-pink-500 text-xs font-medium">{v.invited_by_name}</span>
                            ) : (
                              <span className="text-muted text-xs">—</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {v.interested_to_join ? (
                              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg font-medium">
                                <ThumbsUp className="w-3 h-3" weight="fill" /> Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] bg-gray-100 text-gray-500 px-2 py-1 rounded-lg font-medium">
                                <ThumbsDown className="w-3 h-3" /> No
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
